import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextStyle } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';

export interface SummaryListItem {
    icon: string;
    iconColor: string;
    iconBgColor: string;
    label: string;
    value: string;
    valueColor?: string;
    subValue?: string;
    subValueColor?: string;
    labelStyle?: TextStyle;
    valueStyle?: TextStyle;
}

interface SummaryListCardProps {
    title: string;
    items: SummaryListItem[];
    rightLabel?: string;
    onPress?: () => void;
    showChevron?: boolean;
}

export const SummaryListCard: React.FC<SummaryListCardProps> = ({
    title,
    items,
    rightLabel,
    onPress,
    showChevron = false,
}) => {
    const CardWrapper = onPress ? TouchableOpacity : View;
    const wrapperProps = onPress ? { onPress, activeOpacity: 0.7 } : {};

    return (
        <CardWrapper style={styles.card} {...wrapperProps}>
            <View style={styles.cardTitleRow}>
                <Text style={styles.cardTitle}>{title}</Text>
                {(rightLabel || showChevron) && (
                    <View style={styles.cardTitleRightSection}>
                        {rightLabel && <Text style={styles.rightLabel}>{rightLabel}</Text>}
                        {showChevron && (
                            <Icon name="chevron-right" size={20} color={theme.colors.textLight} />
                        )}
                    </View>
                )}
            </View>

            {items.map((item, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <View style={styles.listDivider} />}
                    <View style={styles.listItem}>
                        <View style={[styles.listIconContainer, { backgroundColor: item.iconBgColor }]}>
                            <Icon name={item.icon} size={20} color={item.iconColor} />
                        </View>
                        <Text style={[styles.listLabel, item.labelStyle]}>{item.label}</Text>
                        {item.subValue ? (
                            <View style={styles.listValueContainer}>
                                <Text style={[styles.listValue, item.valueColor && { color: item.valueColor }, item.valueStyle]}>
                                    {item.value}
                                </Text>
                                <Text style={[styles.listSubValue, item.subValueColor && { color: item.subValueColor }]}>
                                    {item.subValue}
                                </Text>
                            </View>
                        ) : (
                            <Text style={[styles.listValue, item.valueColor && { color: item.valueColor }, item.valueStyle]}>
                                {item.value}
                            </Text>
                        )}
                    </View>
                </React.Fragment>
            ))}
        </CardWrapper>
    );
};

const styles = StyleSheet.create({
    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    cardTitleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    cardTitle: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
    },
    cardTitleRightSection: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
    },
    rightLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        backgroundColor: theme.colors.background,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.md,
        fontSize: 11,
        fontWeight: '600',
    },
    listItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
    },
    listIconContainer: {
        width: 36,
        height: 36,
        borderRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    listLabel: {
        ...theme.typography.body2,
        color: theme.colors.text,
        flex: 1,
    },
    listValue: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    listValueContainer: {
        alignItems: 'flex-end',
    },
    listSubValue: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    listDivider: {
        height: 1,
        backgroundColor: theme.colors.divider,
    },
});

export default SummaryListCard;
